import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ResendConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 「仍要重发」 — the caller resends with `confirm_duplicate_risk: true`
   *  and closes the dialog. */
  onConfirm: () => void;
  /** Cross-account resends: the TARGET account's label, named in the dialog
   *  so the operator sees which number the copy goes out on. `null` = the
   *  message's own account. */
  targetLabel?: string | null;
  /** A resend is in flight — both actions are disabled. */
  pending?: boolean;
}

/**
 * The duplicate-risk gate in front of EVERY resend of an `unknown` (in
 * doubt) message — same- or cross-account (spec 02 §4.5; 2026-09 honest
 * send status).
 *
 * `unknown` means the customer MAY already have the message: the send frame
 * went out unacknowledged, the hub restarted before a queued send was
 * confirmed, or WhatsApp reported an error after sending. A resend can
 * therefore deliver it twice — the hub refuses one without the explicit
 * `confirm_duplicate_risk: true` this dialog stands for.
 *
 * Copy verbatim from the spec. The first focusable (and so the initially
 * focused) control is 「取消」, so a stray Enter never confirms.
 */
export function ResendConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  targetLabel = null,
  pending = false,
}: ResendConfirmDialogProps): React.JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>确认重发</DialogTitle>
          <DialogDescription>
            这条消息的发送结果未知：网关已发出但未确认，客户可能已经收到。重发可能导致客户收到两条相同的消息。确定仍要重发？
          </DialogDescription>
        </DialogHeader>
        {targetLabel && (
          <p data-testid="resend-confirm-target" className="text-sm">
            将通过 <span className="font-extrabold">{targetLabel}</span> 重发
          </p>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <DialogClose asChild>
            <Button type="button" variant="ghost" size="sm" disabled={pending}>
              取消
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={pending}
            onClick={onConfirm}
          >
            仍要重发
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
